import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useGetOrderById, prefetchDeviationsByOrderId } from "@/services/Orders";
import { prefetchClaimsByOrderId } from "@/services/Claims";
import { Order, OrderTabs } from "@/types";
import DrawerTabs, { DrawerTab } from "@/components/atoms/DrawerTabs";
import Drawer from "@/components/molecules/Drawer";
import Loader from "@/components/atoms/Loader";
import { Center, Skeleton } from "@mantine/core";
import DetailsTab from "./TabsContent/DetailsTab";
import DocumentsTab from "@/components/molecules/DrawerOrders/TabsContent/DocumentsTab";
import TransactionTabContent from "@/components/molecules/DrawerOrders/TabsContent/TransactionsTab/TransactionTabContent";
import ClaimsTab from "./TabsContent/ClaimsTab";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import Group from "@/components/atoms/Group";
import Text from "@/components/atoms/Text";
import OrderTypeEnum from "@/constants/enums/OrderTypeEnum";
import DeviationsTabContent from "./TabsContent/DeviationsTab";
import { useAuth } from "@/contexts/AuthContext";
import { useOrdersDrawerStore } from "@/stores/ordersDrawerStore";
import OrderStatusEnum from "@/constants/enums/OrderStatusEnum";

const DrawerOrders = () => {
  const isOpen = useOrdersDrawerStore((s) => s.isOpen);
  const orderId = useOrdersDrawerStore((s) => s.orderId);
  const orderFromStore = useOrdersDrawerStore((s) => s.order);
  const selectedTab = useOrdersDrawerStore((s) => s.selectedTab);
  const setSelectedTab = useOrdersDrawerStore((s) => s.setSelectedTab);
  const close = useOrdersDrawerStore((s) => s.close);

  const { user } = useAuth();
  const { data: orderData, isLoading } = useGetOrderById(orderId as number, undefined, user?.language.id, {
    enabled: !!orderId,
  });
  const order = orderFromStore || orderData;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={close}
      title={order ? <DrawerHeader orderType={order.orderType.id} /> : <Skeleton height={24} width={160} />}
    >
      {isLoading && (
        <Center h="50%">
          <Loader />
        </Center>
      )}
      {!isLoading && order && <Tabs selectedTab={selectedTab} onChangeTab={setSelectedTab} order={order} />}
    </Drawer>
  );
};

export default DrawerOrders;

const DrawerHeader = ({ orderType }: { orderType: number }) => {
  const { t } = useTranslation();

  const { headerText, svgName } =
    orderType === OrderTypeEnum.BUY
      ? {
          headerText: t("common:purchaseOrder"),
          svgName: "purchaseIndicatorLarge",
        }
      : { headerText: t("common:salesOrder"), svgName: "salesIndicatorLarge" };

  return (
    <Group gap="20px" align="center">
      <SVGAsset name={svgName} />
      <Text size="md" display="technical" tt="uppercase" strong={true}>
        {headerText}
      </Text>
    </Group>
  );
};

const Tabs = ({
  selectedTab,
  onChangeTab,
  order,
}: {
  selectedTab: OrderTabs;
  onChangeTab: (tab: OrderTabs) => void;
  order: Order;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return (
    <DrawerTabs value={selectedTab} onChange={onChangeTab as any} keepMounted={false}>
      <DrawerTabs.List>
        <DrawerTabs.Tab value="details">
          <DrawerTab svgName="list" label={t("common:details")} />
        </DrawerTabs.Tab>
        <DrawerTabs.Tab value="transactions">
          <DrawerTab svgName="document" label={t("common:transactions")} />
        </DrawerTabs.Tab>
        <DrawerTabs.Tab value="documents">
          <DrawerTab svgName="document" label={t("common:documents")} />
        </DrawerTabs.Tab>
        <DrawerTabs.Tab
          value="claims"
          data-testid="claims-tab"
          disabled={order.orderType.id !== OrderTypeEnum.BUY}
          onMouseEnter={() => prefetchClaimsByOrderId(queryClient, order.id)}
        >
          <DrawerTab svgName="exclamationWarning" label={t("claims:claims")} />
        </DrawerTabs.Tab>
        <DrawerTabs.Tab
          value="deviations"
          data-testid="deviations-tab"
          disabled={order.orderType.id !== OrderTypeEnum.BUY || order.status === OrderStatusEnum.COMPLETED}
          onMouseEnter={() => prefetchDeviationsByOrderId(queryClient, order.id)}
        >
          <DrawerTab svgName="exclamationWarning" label={t("contracts:contractDeviation.deviation")} />
        </DrawerTabs.Tab>
      </DrawerTabs.List>

      <DrawerTabs.Panel value="details">
        <DetailsTab order={order} />
      </DrawerTabs.Panel>
      <DrawerTabs.Panel value="transactions">
        <TransactionTabContent order={order} />
      </DrawerTabs.Panel>
      <DrawerTabs.Panel value="documents">
        <DocumentsTab order={order} />
      </DrawerTabs.Panel>
      <DrawerTabs.Panel value="claims">
        <ClaimsTab order={order} />
      </DrawerTabs.Panel>
      <DrawerTabs.Panel value="deviations">
        <DeviationsTabContent order={order} />
      </DrawerTabs.Panel>
    </DrawerTabs>
  );
};
